import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { bikeService } from "@/api/bikeService";
import { userService } from "@/api/userService";
import { toast } from "sonner";
import { supabaseBikeService } from "@/api/supabaseBikeService";
import RecentRidesTable from "./components/RecentRidesTable";
import { TimePeriod, getCO2ByBookingType, getCO2Comparison } from "@/utils/dashboardUtils";
import DashboardStats from "./components/DashboardStats";
import MaintenanceBikes from "./components/MaintenanceBikes";
import CO2Statistics from "./components/CO2Statistics";
import DashboardFiltersComponent, { DashboardFilters } from "./components/DashboardFilters";

const DashboardPage: React.FC = () => {
  const [timeFilter, setTimeFilter] = useState<TimePeriod>("year");
  const [filters, setFilters] = useState<DashboardFilters>({
    regionId: "all",
    organizationId: "all",
    siteId: "all"
  });
  
  const bikeStatsQuery = useQuery({
    queryKey: ["bikeStatistics", filters],
    queryFn: () => supabaseBikeService.getBikeStatistics(filters),
    refetchOnWindowFocus: false,
  });
  
  const userStatsQuery = useQuery({
    queryKey: ["userStatistics", filters],
    queryFn: () => userService.getUserStatistics(),
    refetchOnWindowFocus: false,
  });
  
  const co2ByTypeQuery = useQuery({
    queryKey: ["co2ByType", timeFilter, filters],
    queryFn: () => getCO2ByBookingType(timeFilter),
    refetchOnWindowFocus: false,
  });

  const co2ComparisonQuery = useQuery({
    queryKey: ["co2Comparison", timeFilter, filters],
    queryFn: () => getCO2Comparison(timeFilter),
    refetchOnWindowFocus: false,
  });

  React.useEffect(() => {
    if (bikeStatsQuery.error) {
      console.error("Error fetching bike statistics:", bikeStatsQuery.error);
      toast.error("Failed to load bike statistics. Please try refreshing.");
    }
    
    if (userStatsQuery.error) {
      console.error("Error fetching user statistics:", userStatsQuery.error);
      toast.error("Failed to load user statistics. Please try refreshing.");
    }
    
    if (co2ByTypeQuery.error) {
      console.error("Error fetching CO2 offset by booking type:", co2ByTypeQuery.error);
      toast.error("Failed to load CO2 offset by booking type. Please try refreshing.");
    }
    
    if (co2ComparisonQuery.error) {
      console.error("Error fetching CO2 offset comparison:", co2ComparisonQuery.error);
      toast.error("Failed to load CO2 offset comparison. Please try refreshing.");
    }
  }, [bikeStatsQuery.error, userStatsQuery.error, co2ByTypeQuery.error, co2ComparisonQuery.error]);
  
  const isLoading = bikeStatsQuery.isLoading || userStatsQuery.isLoading ||
                   co2ByTypeQuery.isLoading || co2ComparisonQuery.isLoading;

  const handlePeriodChange = (value: string) => {
    if (value) {
      setTimeFilter(value as TimePeriod);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
            Refresh
          </Button>
          <Button asChild size="sm">
            <Link to="/bikes/new">Add New eBike</Link>
          </Button>
        </div>
      </div>
      
      <DashboardFiltersComponent 
        filters={filters}
        onFiltersChange={setFilters}
      />
      
      <DashboardStats
        data={{
          bikeStats: bikeStatsQuery.data,
          userStats: userStatsQuery.data,
        }}
        isLoading={isLoading}
      />
      
      <div className="grid grid-cols-1 gap-6 pt-2 lg:grid-cols-2">
        <MaintenanceBikes isLoading={isLoading} filters={filters} />
        
        <Card>
          <CardContent className="pt-6">
            <RecentRidesTable filters={filters} />
          </CardContent>
        </Card>
      </div>

      <CO2Statistics
        timeFilter={timeFilter}
        onTimeFilterChange={handlePeriodChange}
        co2ByType={co2ByTypeQuery.data || []}
        co2Comparison={co2ComparisonQuery.data || {}}
        isLoading={isLoading}
      />
    </div>
  );
};

export default DashboardPage;
